import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  CalendarToday,
  Leaderboard,
  ArrowForward,
  PhoneCallback,
  HourglassEmpty,
  Language,
  Business,
  Storefront,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { LeadCounts, TabId } from './types';
import { StatCard, tableCardSx, STAT_CARDS_GRID_SX } from './shared';

interface AllTabProps {
  counts: LeadCounts;
  onNavigate: (tab: Exclude<TabId, 'all'>) => void;
}

const SOURCE_ROWS: {
  key: Exclude<TabId, 'all'>;
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: 'booking', label: 'Booking Leads', icon: <PhoneCallback sx={{ color: colors.primary }} /> },
  { key: 'waitlist', label: 'Waitlist', icon: <HourglassEmpty sx={{ color: colors.warning }} /> },
  { key: 'website', label: 'Website Inquiries', icon: <Language sx={{ color: colors.info }} /> },
  { key: 'corporate', label: 'Corporate Leads', icon: <Business sx={{ color: colors.success }} /> },
  { key: 'studioContact', label: 'Studio Contact', icon: <Storefront sx={{ color: colors.primary }} /> },
];

export function AllTab({ counts, onNavigate }: AllTabProps) {
  const totalContacts = counts.booking + counts.waitlist + counts.website + counts.corporate + counts.studioContact;

  const largestSource = useMemo(() => {
    const entries = SOURCE_ROWS.map((row) => ({
      ...row,
      count: counts[row.key],
    }));
    return entries.reduce((max, entry) => (entry.count > max.count ? entry : max), entries[0]);
  }, [counts]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={STAT_CARDS_GRID_SX}>
        <StatCard
          label="Total Contacts"
          value={totalContacts}
          icon={<TrendingUp sx={{ fontSize: 28, color: colors.primary }} />}
          color={colors.primary}
        />
        <StatCard
          label="Booking Leads This Month"
          value={counts.bookingThisMonth ?? 0}
          icon={<CalendarToday sx={{ fontSize: 28, color: colors.info }} />}
          color={colors.info}
        />
        <StatCard
          label={largestSource.label}
          value={largestSource.count}
          subtitle="largest source"
          icon={<Leaderboard sx={{ fontSize: 28, color: colors.warning }} />}
          color={colors.warning}
        />
      </Box>

      <Card sx={{ ...tableCardSx, width: '100%' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} color={colors.textPrimary} mb={2}>
            Leads by Source
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: colors.textSecondary }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 700, color: colors.textSecondary }} align="right">
                  Count
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: colors.textSecondary }} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SOURCE_ROWS.map((row) => (
                <TableRow
                  key={row.key}
                  sx={{
                    '&:hover': { backgroundColor: alpha(colors.primary, 0.04) },
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      {row.icon}
                      <Typography variant="body2" fontWeight={600} color={colors.textPrimary}>
                        {row.label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} color={colors.textPrimary}>
                      {counts[row.key].toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                      onClick={() => onNavigate(row.key)}
                      sx={{
                        textTransform: 'none',
                        borderColor: alpha(colors.primary, 0.4),
                        color: colors.primary,
                        '&:hover': {
                          borderColor: colors.primary,
                          backgroundColor: alpha(colors.primary, 0.06),
                        },
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
}
